import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function EarTraining2() {
  const { lesson, course, accent } = findLesson("ear-training", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET2-Find-Scale-2-Animated.mp4"
        caption="When major fails — swap in natural minor from the same home"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="02" eyebrow="Ear Training · Chapter Two" title="From key to scale: hearing the modal flavour">
        <Prose>
          <p>
            Last chapter we found the <strong>key</strong> — the tonic plus a major-or-minor
            decision. That alone is enough to start playing along with a lot of songs.
            But there's a finer layer underneath: even a song that "sounds major" might be in
            <em> Lydian</em>, <em>Mixolydian</em>, or another mode that swaps out one or two
            notes from the pure major scale. Those swapped notes are the song's
            <strong> fingerprint</strong>.
          </p>
          <p>
            This chapter is about hearing those fingerprint notes — and using them to
            confirm whether the song is in pure major (Ionian), pure minor (Aeolian), or one
            of the modal cousins.
          </p>
        </Prose>

        <MTQuote>
          Music is not really related to a genre or a language, like whether it's a
          Bollywood song, a Spanish song, a Tamil song, I mean, how does that matter? The
          chords exist, the 12 notes exist for all humans who play this art form.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The diagnostic notes" title="The 3rd, 4th, 6th and 7th tell the whole story">
        <Prose>
          <p>
            Among the seven notes of any scale, four are diagnostic — they're the ones that
            change between modes. The 1, 2 and 5 stay constant in almost every common scale,
            so they tell you nothing about modal flavour. Listen instead for these four:
          </p>
        </Prose>

        <Glossary>
          <Term name="The 3rd" def="Major 3rd → bright (Ionian, Lydian, Mixolydian). Minor 3rd → dark (Aeolian, Dorian, Phrygian)." />
          <Term name="The 4th" def="Natural 4th → most common. Sharp 4th (♯4) → Lydian — the dreamy, suspended-in-air sound (Simpsons theme, Yoda's theme)." />
          <Term name="The 6th" def="Inside a minor key, a major 6th = Dorian (jazzy, Scarborough Fair). A minor 6th = Aeolian (the standard pop-minor)." />
          <Term name="The 7th" def="Inside a major key, a flat 7th (♭7) = Mixolydian — the rock/blues/Celtic flavour (Sweet Child o' Mine intro). Inside a minor key, a natural 7th = harmonic minor — the dramatic, Spanish-flamenco flavour." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The four modes you'll meet most" title="Listen, then check">
        <Prose>
          <p>
            Out of seven modes, four account for the overwhelming majority of what you'll
            ever transcribe. Master these and the rest are refinements. Use the explorer
            below to drop into each and listen to the difference:
          </p>
        </Prose>

        <Steps>
          <Step><strong>Ionian (the major scale).</strong> 1 2 3 4 5 6 7. The default. If nothing sounds "off", it's Ionian.</Step>
          <Step><strong>Mixolydian.</strong> 1 2 3 4 5 6 ♭7. The flat 7 is the giveaway — listen for that "rock" sound. Hendrix, Black Crowes, the Grateful Dead.</Step>
          <Step><strong>Aeolian (natural minor).</strong> 1 2 ♭3 4 5 ♭6 ♭7. The default minor. If "minor" alone fits, it's Aeolian.</Step>
          <Step><strong>Dorian.</strong> 1 2 ♭3 4 5 6 ♭7. Like Aeolian but with a brighter major 6 instead of the flat 6. Santana, Steely Dan, jazz standards.</Step>
        </Steps>

        <ScaleExplorer tonic="C" scales={["major", "mixolydian", "naturalMinor", "dorian"]} />
      </Chapter>

      <Chapter eyebrow="The chord-family shortcut" title="Chords also fingerprint the mode">
        <Prose>
          <p>
            If you can identify two or three chords from the song, you can often pin the
            mode by which chords are present. A song that uses <strong>I, ♭VII, IV</strong>{" "}
            is almost certainly Mixolydian. A song that uses <strong>i, ♭VII, IV</strong>{" "}
            is Dorian. A song with <strong>i, iv, V (major)</strong> is harmonic minor.
          </p>
        </Prose>

        <ChordTree tonic="G" mode="major" />

        <Prose>
          <p>
            Listen carefully to the song's chord progression. Try to identify the
            <em> functional role</em> of each chord (where does it pull you?). Chords have
            their own gravity — Tonic chords feel like home, Pre-dominant chords feel like
            "going on a journey", Dominant chords feel like "we need to go home".
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/02a-functional-harmony-Tonic-PreDominant-Dominant.png"
          caption="Jason's note: functional harmony in any major key. Tonic chords (I, vi, iii) feel like home. Pre-Dominant chords (ii, IV) lift you out of home. Dominant chords (V, vii°, V7) pull you violently back. The mediant (iii) sits in the gap. Once you can hear this gravity, you can transcribe almost anything."
        />
      </Chapter>

      <Chapter eyebrow="The cadence test" title="Endings tell you the truth">
        <Prose>
          <p>
            One last technique: <strong>listen to how phrases end</strong>. Cadences are the
            chord-pair that finishes a section, and there are only four common types:
          </p>
        </Prose>

        <Glossary>
          <Term name="Authentic / Perfect: V → I" def="The strongest 'we are home' resolution. D → G in G major. The classical default." />
          <Term name="Plagal / 'Amen' cadence: IV → I" def="C → G in G major. Softer, churchy. Beatles' 'Let It Be' end." />
          <Term name="Deceptive: V → vi" def="D → Em in G major. The ear expects I, gets vi instead. A cliffhanger." />
          <Term name="Half cadence: ? → V" def="Any chord landing on V at the end of a phrase. Feels unfinished, ready for the next phrase." />
        </Glossary>

        <NoteImage
          src="/notes/ear-training/finals/02b-cadences-G-major-examples.png"
          caption="Jason's cadence card in G major: Authentic Perfect (V→I = D→G), Plagal/Amen (IV→I = C→G), Deceptive (V→vi = D→Em), Half (?→V = ?→D). Listen for which one ends each chorus and you've identified the song's harmonic personality."
        />

        <TheoryBox label="The full key-finding workflow">
          1. Find the tonic (last note of chorus, first chord of chorus).<br />
          2. Hum the major 3rd → if it fits, Ionian. If not, hum the minor 3rd.<br />
          3. Test the 4th and 7th to distinguish modes (Lydian = ♯4, Mixolydian = ♭7, Dorian = ♭3 + ♮6).<br />
          4. Listen to the cadence to confirm — if it ends V→I in major, it's Ionian. If it lingers on V, it's modal.
        </TheoryBox>
      </Chapter>

      <NextUp
        to="/lesson/ear-training-3"
        label="Chapter 3 · How to FEEL time (Beat Division)"
        why="Pitch is half the story; rhythm is the other. Next we'll learn to feel whether a song divides each beat in 2, 3, or 4 — and what straight vs swing actually means to your body."
      />
    </LessonShell>
  );
}
